import { ITextParserContext, Text, TextPart } from '@features/lp/lp-text/lp-text.utils';
import { AToken, BoldToken, IndentToken, ItalicToken, TextToken, WaveToken } from '@features/lp/lp-text/parser/tokens';

export class TextParser {
  public static parse(text: string, ctx: ITextParserContext) {
    const tokens = TextParser.tokenize(text, ctx);
    const parts = TextParser.parseTokens(tokens);
    return new Text(parts);
  }

  public static tokenize(input: string, ctx: ITextParserContext): AToken[] {
    const regex = /(<b>|<\/b>|<i>|<\/i>|<wave>|<\/wave>|<indent=\d+(\.|%)>|\{([\w\W]+?)\})/g;
    const tokens: AToken[] = [];
    let lastIndex = 0;

    for (const match of input.matchAll(regex)) {
      const [fullMatch] = match;
      const index = match.index!;

      if (index > lastIndex) {
        tokens.push(new TextToken(input.slice(lastIndex, index)));
      }

      if (fullMatch === '<b>') {
        tokens.push(new BoldToken(true));
      } else if (fullMatch === '</b>') {
        tokens.push(new BoldToken(false));
      } else if (fullMatch === '<i>') {
        tokens.push(new ItalicToken(true));
      } else if (fullMatch === '</i>') {
        tokens.push(new ItalicToken(false));
      } else if (fullMatch === '<wave>') {
        tokens.push(new WaveToken(true));
      } else if (fullMatch === '</wave>') {
        tokens.push(new WaveToken(false));
      } else if (fullMatch.startsWith('{') && fullMatch.endsWith('}')) {
        const name = fullMatch.slice(1, -1);
        const value = ctx.variables[name];
        tokens.push(...TextParser.tokenize(value ?? '', ctx));
      }

      lastIndex = index + fullMatch.length;
    }

    if (lastIndex < input.length) {
      tokens.push(new TextToken(input.slice(lastIndex)));
    }

    return tokens;
  }

  public static parseTokens(tokens: AToken[]): TextPart[] {
    const parts: TextPart[] = [];
    let currentStyle: Record<string, any> = {};
    let currentContent = '';
    let isWave = false;
    let waveIndex = 0;
    const waveDelay = 0.05;

    const createParts = (content: string, style: Record<string, any>): TextPart[] => {
      if (!isWave) return [new TextPart(content, style)];

      const waveParts: TextPart[] = [];
      for (const c of content.split('')) {
        waveParts.push(
          new TextPart(c, { ...structuredClone(style), animation: `wave 1s infinite ${-1 / waveDelay + waveIndex * waveDelay}s` }),
        );
        waveIndex++;
      }
      return waveParts;
    };

    for (const token of tokens) {
      if (token instanceof TextToken) {
        parts.push(...createParts(token.value, structuredClone(currentStyle)));
      } else if (token instanceof BoldToken) {
        if (token.value) {
          // @ts-ignore
          currentStyle.fontWeight = 'bold';
        } else {
          // @ts-ignore
          delete currentStyle.fontWeight;
        }
      } else if (token instanceof ItalicToken) {
        if (token.value) {
          // @ts-ignore
          currentStyle.fontStyle = 'italic';
        } else {
          // @ts-ignore
          delete currentStyle.fontStyle;
        }
      } else if (token instanceof WaveToken) {
        if (token.value) {
          // @ts-ignore
          currentStyle.display = 'inline-block';
          isWave = true;
        } else {
          // @ts-ignore
          delete currentStyle.display;
          isWave = false;
        }
      } else if (token instanceof IndentToken) {
        currentStyle['margin-left'] = `${token.indent}px`;
      }
    }

    if (currentContent) {
      parts.push(...createParts(currentContent, structuredClone(currentStyle)));
    }

    return parts;
  }
}
